import {
  TRADE_CATEGORIES,
  getCategoryInfo,
  type TradeCategory,
  type AiInquiryDraft,
} from '../types/poptavky'

export interface UrlInquiryPrefill {
  hasAnyParam: boolean
  aiPrompt?: string
  categories?: TradeCategory[]
  title?: string
  description?: string
  subTasks?: string[]
  scaleSummary?: string
  city?: string
  zip?: string
  street?: string
  urgency?: 'flexible' | 'within_month' | 'urgent'
  expectedDate?: string
  estimatedBudget?: string
  clientName?: string
  clientPhone?: string
  clientEmail?: string
  initialStep?: 1 | 2 | 3
}

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

const VALID_CATEGORY_IDS = new Set<string>(TRADE_CATEGORIES.map((c) => c.id))

export function resolveCategoryTokens(rawInput: string): TradeCategory[] {
  if (!rawInput.trim()) return []
  const tokens = rawInput
    .split(/[,;|+]+/)
    .map((s) => s.trim())
    .filter(Boolean)

  const matched: TradeCategory[] = []

  for (const token of tokens) {
    if (VALID_CATEGORY_IDS.has(token)) {
      matched.push(token as TradeCategory)
      continue
    }
    const norm = normalizeText(token)
    // Match by label or keyword
    const exactOrPartial = TRADE_CATEGORIES.find(
      (c) =>
        normalizeText(c.id) === norm ||
        normalizeText(c.label).includes(norm) ||
        norm.includes(normalizeText(c.label.split(',')[0]))
    )
    if (exactOrPartial) {
      matched.push(exactOrPartial.id)
      continue
    }
    if (norm.includes('elektr')) matched.push('electrical')
    else if (norm.includes('vod') || norm.includes('instalat')) matched.push('plumbing')
    else if (norm.includes('topen') || norm.includes('kotel') || norm.includes('cerpadl')) matched.push('heating')
    else if (norm.includes('plyn')) matched.push('gas_hvac')
    else if (norm.includes('kuchyn')) matched.push('kitchen')
    else if (norm.includes('koupeln') || norm.includes('jadr')) matched.push('bathroom')
    else if (norm.includes('obklad') || norm.includes('dlazb')) matched.push('tiling')
    else if (norm.includes('podlah') || norm.includes('vinyl') || norm.includes('parket')) matched.push('flooring')
    else if (norm.includes('malov') || norm.includes('malir')) matched.push('painting')
    else if (norm.includes('zedni') || norm.includes('omitk') || norm.includes('stavb')) matched.push('masonry')
    else if (norm.includes('sadrokarton') || norm.includes('sdk')) matched.push('drywall_attic')
    else if (norm.includes('fasad') || norm.includes('zatepl')) matched.push('facades_insulation')
    else if (norm.includes('strech') || norm.includes('pokryvac')) matched.push('roofing')
    else if (norm.includes('okn') || norm.includes('dver')) matched.push('windows_doors')
    else if (norm.includes('zahrad') || norm.includes('trav') || norm.includes('sekan')) matched.push('garden_mowing')
    else if (norm.includes('strom') || norm.includes('kacen')) matched.push('tree_felling_expert')
    else if (norm.includes('plot')) matched.push('fencing_gates')
    else if (norm.includes('stehov')) matched.push('moving_apartments')
    else if (norm.includes('vykliz')) matched.push('clearing_cellars')
    else if (norm.includes('uklid')) matched.push('home_cleaning')
    else if (norm.includes('rekonstrukc')) matched.push('reconstruction')
  }

  return Array.from(new Set(matched))
}

/**
 * Zparsuje URL parametry (např. vygenerované z ChatGPT, Gemini, Claude nebo Perplexity)
 * do strukturovaného předvyplnění poptávky.
 */
export function parseUrlInquiryParams(search: string = window.location.search): UrlInquiryPrefill {
  const params = new URLSearchParams(search)

  const rawObor =
    params.get('obor') ||
    params.get('obory') ||
    params.get('category') ||
    params.get('categories') ||
    ''
  const categories = resolveCategoryTokens(rawObor)

  const aiPrompt =
    params.get('ai') ||
    params.get('prompt') ||
    params.get('q') ||
    params.get('zadani') ||
    undefined

  const title = params.get('nazev') || params.get('title') || undefined
  const description = params.get('popis') || params.get('description') || undefined

  const rawSubTasks = params.get('ukony') || params.get('subTasks') || ''
  const subTasks = rawSubTasks
    ? rawSubTasks
        .split(/[|;]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    : undefined

  const scaleSummary = params.get('rozsah') || params.get('scale') || undefined
  const city = params.get('mesto') || params.get('city') || undefined
  const zip = params.get('psc') || params.get('zip') || undefined
  const street = params.get('ulice') || params.get('street') || undefined

  const rawUrgency = params.get('termin') || params.get('urgency') || ''
  const urgency: 'flexible' | 'within_month' | 'urgent' | undefined =
    rawUrgency === 'urgent' || rawUrgency === 'ihned'
      ? 'urgent'
      : rawUrgency === 'flexible' || rawUrgency === 'nespecha'
      ? 'flexible'
      : rawUrgency === 'within_month'
      ? 'within_month'
      : undefined

  const expectedDate = params.get('datum') || params.get('expectedDate') || undefined
  const estimatedBudget = params.get('rozpocet') || params.get('budget') || undefined

  const clientName = params.get('jmeno') || params.get('name') || undefined
  const clientPhone = params.get('telefon') || params.get('phone') || undefined
  const clientEmail = params.get('email') || undefined

  const rawStep = params.get('krok') || params.get('step')
  let initialStep: 1 | 2 | 3 | undefined =
    rawStep === '3' ? 3 : rawStep === '2' ? 2 : rawStep === '1' ? 1 : undefined

  // Pokud AI předvyplnilo název, popis a město, můžeme rovnou přepnout na Krok 3 (Potvrzení a kontakt)
  if (!initialStep && title && description && city) {
    initialStep = 3
  }

  const hasAnyParam = Boolean(
    categories.length > 0 ||
      aiPrompt ||
      title ||
      description ||
      city ||
      clientName ||
      clientPhone ||
      clientEmail
  )

  return {
    hasAnyParam,
    aiPrompt: aiPrompt?.trim(),
    categories: categories.length > 0 ? categories : undefined,
    title: title?.trim(),
    description: description?.trim(),
    subTasks,
    scaleSummary: scaleSummary?.trim(),
    city: city?.trim(),
    zip: zip?.trim(),
    street: street?.trim(),
    urgency,
    expectedDate: expectedDate?.trim(),
    estimatedBudget: estimatedBudget?.trim(),
    clientName: clientName?.trim(),
    clientPhone: clientPhone?.trim(),
    clientEmail: clientEmail?.trim(),
    initialStep,
  }
}

/**
 * Převede URL parametry na objekt AiInquiryDraft, pokud obsahuje předvyplněná pole.
 */
export function buildDraftFromUrlPrefill(prefill: UrlInquiryPrefill): AiInquiryDraft | null {
  if (!prefill.title && !prefill.description && (!prefill.categories || prefill.categories.length === 0)) {
    return null
  }
  const cats: TradeCategory[] =
    prefill.categories && prefill.categories.length > 0 ? prefill.categories : ['other']
  const primaryInfo = getCategoryInfo(cats[0])

  return {
    prompt: prefill.aiPrompt || prefill.title || '',
    categories: cats,
    title: prefill.title || primaryInfo.placeholderTitle,
    description: prefill.description || primaryInfo.placeholderDesc,
    subTasks: prefill.subTasks || [],
    scaleSummary: prefill.scaleSummary || 'Střední rozsah zakázky',
    estimatedBudget: prefill.estimatedBudget || 'Dle dohody / nabídek',
    expectedDate: prefill.expectedDate || 'Během 1 měsíce',
    city: prefill.city,
  }
}

/**
 * Dynamická správa SEO meta tagů (<title>, description, OpenGraph, canonical)
 * při pohybu po aplikaci a výběru řemesla.
 */
export function updateDocumentSeo(params: {
  view: 'landing' | 'new_inquiry' | 'my_inquiries' | 'admin' | 'review_form'
  category?: TradeCategory
  categories?: TradeCategory[]
}) {
  const baseDomain = 'https://nacenito.cz'
  let title =
    'NaceniTo.cz – Zadejte poptávku řemeslníků, firem a brigádníků zdarma | Srovnání nabídek'
  let description =
    'Potřebujete řemeslníka, stavební firmu nebo brigádníka? Popište jednou větou, co potřebujete udělat – AI na NaceniTo.cz vám připraví poptávku a ověřené firmy z okolí pošlou cenové nabídky zdarma.'
  let canonicalPath = '/'

  if (params.view === 'new_inquiry') {
    const activeCat = params.categories?.[0] || params.category
    if (activeCat) {
      const info = getCategoryInfo(activeCat)
      title = `${info.label} – Zadat poptávku a zjistit cenu zdarma | NaceniTo.cz`
      description = `${info.shortDesc} Zadejte poptávku v oboru ${info.label} na NaceniTo.cz zdarma a získejte cenové nabídky od ověřených řemeslníků a firem ve vašem okolí.`
      canonicalPath = `/zadat-poptavku?obor=${activeCat}`
    } else {
      title = 'Zadat poptávku řemeslníka nebo firmy zdarma (AI šablona) | NaceniTo.cz'
      description =
        'Vytvořte poptávku řemeslných, stavebních nebo zahradních prací do 1 minuty. AI asistent za vás automaticky vyplní rozsah prací, obory i nákres půdorysu.'
      canonicalPath = '/zadat-poptavku'
    }
  } else if (params.view === 'my_inquiries') {
    title = 'Moje poptávky a cenové nabídky | NaceniTo.cz'
    description = 'Přehled vašich zadaných poptávek a doručených cenových nabídek od řemeslníků a firem na NaceniTo.cz.'
    canonicalPath = '/moje-poptavky'
  } else if (params.view === 'review_form') {
    title = 'Ohodnotit realizaci a řemeslníka | NaceniTo.cz'
    description = 'Zanechte ověřené hodnocení realizované zakázky na portálu NaceniTo.cz.'
    canonicalPath = '/hodnoceni'
  }

  document.title = title

  const setMeta = (selector: string, attr: 'content' | 'href', val: string) => {
    const el = document.querySelector(selector)
    if (el) el.setAttribute(attr, val)
  }

  setMeta('meta[name="description"]', 'content', description)
  setMeta('meta[property="og:title"]', 'content', title)
  setMeta('meta[property="og:description"]', 'content', description)
  setMeta('meta[property="og:url"]', 'content', `${baseDomain}${canonicalPath}`)
  setMeta('meta[name="twitter:title"]', 'content', title)
  setMeta('meta[name="twitter:description"]', 'content', description)
  setMeta('link[rel="canonical"]', 'href', `${baseDomain}${canonicalPath}`)
}

/**
 * Registrace WebMCP (window.navigator.modelContext) a globálního objektu window.NaceniToAI
 * pro přímou interakci AI agentů v prohlížeči (Gemini in Chrome, ChatGPT Operator, Claude).
 */
export function registerBrowserAiAgentTools(callbacks: {
  onOpenPrefilledWizard: (prefill: UrlInquiryPrefill) => void
}) {
  if (typeof window === 'undefined') return

  const apiEndpoint = 'https://europe-west1-hk-software-c5e52.cloudfunctions.net/createAiAgentInquiry'

  const naceniToAiApi = {
    version: '1.0.0',
    service: 'NaceniTo.cz',
    operator: 'hkcloud.cz',
    apiEndpoint,
    openApiSpec: 'https://nacenito.cz/openapi.json',
    llmsTxt: 'https://nacenito.cz/llms.txt',
    listCategories: () =>
      TRADE_CATEGORIES.map((c) => ({
        id: c.id,
        label: c.label,
        group: c.group,
        shortDesc: c.shortDesc,
        subTasks: c.subTasks,
      })),
    prefillInquiryInUi: (params: Partial<UrlInquiryPrefill>) => {
      callbacks.onOpenPrefilledWizard({
        hasAnyParam: true,
        ...params,
      })
      return {
        ok: true,
        message: 'Formulář poptávky byl v rozhraní NaceniTo.cz předvyplněn a otevřen.',
      }
    },
    createInquiryDirect: async (payload: {
      title?: string
      description?: string
      prompt?: string
      categories?: string[]
      city?: string
      zip?: string
      estimatedBudget?: string
      clientName?: string
      clientPhone?: string
      clientEmail?: string
      agentName?: string
    }) => {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      return await response.json()
    },
  }

  ;(window as unknown as Record<string, unknown>).NaceniToAI = naceniToAiApi

  // Podpora standardu WebMCP (navigator.modelContext) pro AI prohlížeče
  const nav = window.navigator as unknown as {
    modelContext?: {
      registerTool?: (tool: unknown) => void
    }
  }
  if (nav.modelContext && typeof nav.modelContext.registerTool === 'function') {
    try {
      nav.modelContext.registerTool({
        name: 'nacenito_create_inquiry',
        description:
          'Vytvoří nebo předvyplní poptávku řemeslníků a stavebních firem na portálu NaceniTo.cz jménem uživatele.',
        inputSchema: {
          type: 'object',
          properties: {
            title: { type: 'string', description: 'Název poptávky' },
            description: { type: 'string', description: 'Podrobný popis prací' },
            categories: {
              type: 'array',
              items: { type: 'string' },
              description: 'Kódy oborů (např. electrical, plumbing, bathroom, kitchen)',
            },
            city: { type: 'string', description: 'Město realizace v ČR' },
            estimatedBudget: { type: 'string', description: 'Orientační rozpočet' },
            clientName: { type: 'string', description: 'Jméno zákazníka' },
            clientPhone: { type: 'string', description: 'Telefon zákazníka' },
            clientEmail: { type: 'string', description: 'E-mail zákazníka' },
          },
          required: ['title', 'description', 'city'],
        },
        execute: async (args: Record<string, unknown>) => {
          return await naceniToAiApi.createInquiryDirect(args)
        },
      })
    } catch {
      // Ignore if browser modelContext implementation differs
    }
  }
}
